import { FormattedMessage } from 'react-intl'

import type { Step, StepType } from 'containers/Flow/Step'
import type { StepHandlers } from 'containers/Flow/stepHandler'

import { ButtonSolid } from 'components/Buttons'
import { Card } from 'components/Layout'

import { ButtonGroup } from '../ButtonGroup'
import { Container } from '../Container'
import { Wrapper } from '../Wrapper'

type Props = TaggedType<Step, StepType.Terms> & {
	onNext: StepHandlers['onTermsAccept']
}

export const Terms = ({ budgetId, brandMetadata, brandFeatures, userMetadata, onNext }: Props) => {
	const { applicantName, isPrimary } = userMetadata

	return (
		<Wrapper>
			<Card>
				<Container>
					<h1>
						<FormattedMessage id='flow.terms.title' defaultMessage='Consent' />
					</h1>
					<p>
						{isPrimary ? (
							<FormattedMessage
								id='flow.terms.descriptionOne'
								defaultMessage='I hereby consent to {bankName} collecting and processing transaction data from my selected accounts via Subaio. The purpose is to create an overview of my finances, which will be used to assess my creditworthiness. I understand that this involves analysing my income and expenses and that the results will be shared with {bankName}.'
								values={{ bankName: brandMetadata.title }}
							/>
						) : (
							<FormattedMessage
								id='flow.terms.descriptionOneCoapplicant'
								defaultMessage='I hereby consent to {bankName} collecting and processing transaction data from my selected accounts via Subaio. The purpose is to create an overview of my and {applicantName}{pluralNotation} finances, which will be used to assess my/our creditworthiness. I understand that this involves an analysis of my/our income and expenses and that the information and results will be shared with {applicantName} and {bankName}.'
								values={{
									bankName: brandMetadata.title,
									applicantName,
									pluralNotation: /^[a-z|A-Z|0-9]+[^I]\s?s{1}$/.test(applicantName) ? "'" : 's',
								}}
							/>
						)}
					</p>
					<p>
						{isPrimary ? (
							<FormattedMessage
								id='flow.terms.descriptionTwo'
								defaultMessage="I am aware that my data will be stored for as long as a customer relationship with {bankName} exists. In the event that a customer relationship is not established, data is deleted according to the bank's general rules. Data is deleted by Subaio 30 days after the results have been shared with {bankName}."
								values={{ bankName: brandMetadata.title }}
							/>
						) : (
							<FormattedMessage
								id='flow.terms.descriptionTwoCoapplicant'
								defaultMessage='I am aware that my data will be stored as long as a customer relationship with {bankName} exists. In the event that a customer relationship is not established, data will be deleted according to the general rules of financial institutions. Data will be deleted by Subaio 30 days after the results have been shared with {applicantName} and {bankName}.'
								values={{ bankName: brandMetadata.title, applicantName }}
							/>
						)}
					</p>
					<p>
						<FormattedMessage
							id='flow.terms.descriptionThree'
							defaultMessage='I am also informed that I have the option to withdraw my consent at any time.'
						/>
					</p>
					<p>
						<FormattedMessage
							id='flow.terms.descriptionFour'
							defaultMessage='I understand that a revocation will not affect the legality of the processing of my data that took place before the revocation or which is necessary in relation to the documentation of decision-making bases.'
						/>
					</p>
					<p>
						<FormattedMessage
							id='flow.terms.descriptionFive'
							defaultMessage='Further information on how my personal data is handled can be found at any time in the Terms for the processing of personal data found on {bankName}{pluralNotation} website.'
							values={{
								bankName: brandMetadata.title,
								pluralNotation: /^[a-z|A-Z|0-9]+[^I]\s?s{1}$/.test(brandMetadata.title) ? "'" : 's',
							}}
						/>
					</p>
				</Container>
				<ButtonGroup>
					<ButtonSolid
						label={<FormattedMessage id='flow.terms.buttonAccept' defaultMessage='I accept' />}
						onClick={() => onNext(budgetId, brandFeatures, userMetadata)}
					/>
				</ButtonGroup>
			</Card>
		</Wrapper>
	)
}
